import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CustomCategory } from './useFetchCustomCategories';
import type { AgentApiCustomCategoriesIndex } from '@/types/serializers';

type CustomizeType = 'candidate_profile' | 'company' | 'candidate_interview_log';

interface ReorderCustomCategoriesParams {
  recordClassName: string;
  categoryOrders: Array<{ id: number; order_number: number }>;
}

/**
 * カスタムカテゴリの順序変更用のフック
 */
export default function useReorderCustomCategories() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ recordClassName, categoryOrders }: ReorderCustomCategoriesParams) => {
      const response = await fetch('/agent/api/custom_categories/reorder', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token':
            document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        body: JSON.stringify({
          record_class_name: recordClassName,
          category_orders: categoryOrders,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.errors?.[0] || '順序の更新に失敗しました');
      }

      const data: AgentApiCustomCategoriesIndex = await response.json();
      return { recordClassName, categories: data.records };
    },
    onSuccess: (data) => {
      // カテゴリ一覧のキャッシュを更新
      // recordClassNameからtypeを逆引きする必要がある
      const RECORD_CLASS_NAME_TO_TYPE: Record<string, CustomizeType> = {
        CandidateProfile: 'candidate_profile',
        Company: 'company',
        CandidateInterviewLog: 'candidate_interview_log',
      };

      const type = RECORD_CLASS_NAME_TO_TYPE[data.recordClassName];
      if (type) {
        queryClient.setQueryData<CustomCategory[]>(['custom_categories', type], () => {
          return data.categories.map((record) => ({
            id: record.id,
            name: record.name,
            orderNumber: record.order_number,
            recordClassName: record.record_class_name,
          }));
        });
      }
    },
  });
}
